<template>
  <organism-create-edit-template
    v-model="templateData"
    :escalation-template-id="templateId || null"
    :userOptions="userOptions"
    :internalUserData="internalUserData"
    @createNewEscalation="createNewEscalation"
    @updateEscalation="updateEscalation"
  ></organism-create-edit-template>
</template>

<script>
import OrganismCreateEditTemplate from "@/components/organisms/portal/internal/escalation/OrganismCreateEditTemplate.vue"

import {
  createNewTicketEscalation,
  getTicketEscalationById,
  updateTicketEscalation,
} from "@/api/module-1/escalation"
import mixins from "@/mixins"
import { getAllUserList } from "@/api/api-module1"
import { mapGetters } from "vuex"

export default {
  name: "CreateEditTemplate",
  components: { OrganismCreateEditTemplate },
  mixins: [mixins],
  data() {
    return {
      templateData: {
        templateId: null,
        templateName: null,
        templateDescription: null,
        status: null,
        isEscalate: false,
        criteria: [
          {
            id: "NewLevel1",
            contactName: null,
            contactRole: "",
            contactEmail: null,
            contactPhone: null,
            escalationTimeSummary: null,
            escalationTimeDays: null,
            escalationTimeHours: null,
            escalationTimeMinutes: null,
            enableSystemNotification: false,
            enableEmailNotification: false,
            emailAddress: "",

            emailOptions: [],
            phoneOptions: [],
          },
        ],
      },

      userOptions: [],
      internalUserData: [],
    }
  },

  computed: {
    ...mapGetters("siteStore", ["siteOptions"]),

    templateId() {
      return this.$route?.params.escalationTemplateId
    },
  },

  async mounted() {
    await this.getInternalUser()

    if (this.templateId) {
      await this.getEscalationDetail()
    }
  },

  methods: {
    formatDataEscalation(templateData) {
      const levelFormat = templateData?.criteria?.map((item, index) => {
        return {
          level: index + 1,
          name: item.contactName.label,
          role: item.contactRole,
          contactEmail: item.contactEmail.email,
          contactPhone: item.contactPhone.phone,
          escalationTimeMinute: this.convertTimeToMinutes(
            item.escalationTimeDays,
            item.escalationTimeHours,
            item.escalationTimeMinutes
          ),
          enableSystemNotification: item.enableSystemNotification,
          enableEmailNotification: item.enableEmailNotification,
          username: item.contactName.username,
          email: item?.emailAddress?.email?.trim(),
        }
      })

      return {
        escalationId: this.templateId,
        name: templateData.templateName.trim(),
        description: templateData.templateDescription.trim(),
        status: templateData.status,
        levels: levelFormat,
      }
    },

    async createNewEscalation() {
      this.templateData.status = "ACTIVE"
      const data = this.formatDataEscalation(this.templateData)

      this.showLoading(true)
      await createNewTicketEscalation(data)
        .then((response) => {
          this.toastSuccess("Create Ticket Escalation Successfully.")
          const templateId = response?.data

          this.$router.push(`/internal/web-portal/escalation/${templateId}`)
        })
        .catch(() => this.toastError("Create Ticket Escalation Failed."))
        .finally(() => this.showLoading(false))
    },

    async getEscalationDetail() {
      this.showLoading(true)
      await getTicketEscalationById(this.templateId)
        .then(async (response) => {
          const levels = await Promise.all(
            response.levels?.map(async (item) => {
              const escalationTime = this.convertMinutesToDays(
                item.escalationTimeMinute
              )

              const user = this.userOptions?.find(
                (option) => option.username === item.username
              )

              const contactEmail = user?.emails?.find(
                (emailItem) => emailItem.email === item.contactEmail
              )
              const contactPhone = user?.phones?.find(
                (phoneItem) => phoneItem.phone === item.contactPhone
              )
              const emailAddress = user?.emails?.find(
                (emailItem) => emailItem.email === item.email
              )

              return {
                id: item.level,
                level: item.level,
                contactName: user || { label: item.name },
                contactRole: item.role,
                contactEmail: contactEmail || { email: item.contactEmail },
                contactPhone: contactPhone || { phone: item.contactPhone },
                escalationTimeSummary: item.escalationTimeMinute,
                escalationTimeDays: escalationTime.days,
                escalationTimeHours: escalationTime.hours,
                escalationTimeMinutes: escalationTime.mins,
                enableSystemNotification: item.enableSystemNotification,
                enableEmailNotification: item.enableEmailNotification,
                emailAddress: emailAddress || { email: item.email },

                emailOptions: user?.emails || [],
                phoneOptions: user?.phones || [],
              }
            })
          )
          levels.sort((a, b) => a.level - b.level)

          this.templateData = {
            templateId: response.escalationId,
            templateName: response.name,
            templateDescription: response.description,
            status: response.status,
            isEscalate: response.isEscalated,
            criteria: levels,
          }
        })
        .finally(() => this.showLoading(false))
    },

    updateEscalation(templateData) {
      this.showLoading(true)
      const data = this.formatDataEscalation(templateData)

      updateTicketEscalation(this.templateId, data)
        .then(() => {
          this.toastSuccess("Update Ticket Escalation Successfully.")
          this.$router.push(
            `/internal/web-portal/escalation/${this.templateId}`
          )
        })
        .catch(() => this.toastError("Update Ticket Escalation Failed."))
        .finally(() => this.showLoading(false))
    },

    convertTimeToMinutes(days, hours, minutes) {
      if (!days && !hours && !minutes) return 0

      return days * 60 * 24 + hours * 60 + Number(minutes)
    },

    convertMinutesToDays(minuteTotal) {
      if (!minuteTotal) return { days: 0, hours: 0, mins: 0 }

      const mins = minuteTotal % 60
      const hour = Math.floor(minuteTotal / 60)
      const days = Math.floor(hour / 24)
      const hours = hour % 24

      return { days, hours, mins }
    },

    async getInternalUser() {
      this.showLoading(true)
      const filter = {
        siteIds: this.siteOptions.map((site) => site.value),
      }
      await getAllUserList(filter)
        .then(async (response) => {
          this.internalUserData = response
          this.userOptions = response.map((user) => {
            return {
              username: user.username,
              label: `${user.firstName} ${user.lastName}`,
            }
          })
        })
        .finally(() => {
          this.showLoading(false)
        })
    },
  },
}
</script>

<style lang="scss" scoped></style>
